<template>
  <Transition name="group-chats-slide">
    <div v-if="isOpen" class="group-chats">
      <div class="group-chats__panel" role="dialog" aria-label="Chats de grupo">
        <header class="group-chats__header">
          <v-menu
            v-if="activeGroups.length > 1"
            location="bottom start"
            :z-index="2600"
            scrim="false"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                variant="text"
                color="white"
                class="group-chats__switch"
                aria-label="Cambiar de grupo"
              >
                <span class="group-chats__switch-label">{{ selectedGroupName }}</span>
                <v-icon end size="20">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list density="compact" class="group-chats__menu">
              <v-list-item
                v-for="group in activeGroups"
                :key="group.groupId"
                :active="group.groupId === selectedGroupId"
                @click="selectGroup(group.groupId)"
              >
                <v-list-item-title>{{ group.name }}</v-list-item-title>
                <template v-if="unreadCountFor(group.groupId) > 0" #append>
                  <v-badge
                    inline
                    color="error"
                    :content="unreadCountFor(group.groupId) > 99 ? '99+' : unreadCountFor(group.groupId)"
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
          <div v-else class="group-chats__title">{{ selectedGroupName }}</div>
          <div class="group-chats__header-actions">
            <v-btn
              v-if="selectedGroupId"
              variant="text"
              size="small"
              color="white"
              class="group-chats__go"
              prepend-icon="mdi-arrow-right"
              @click="goToGroup"
            >
              Ir al grupo
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              color="white"
              aria-label="Cerrar chats"
              @click="close"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </header>

        <div class="group-chats__hint">*negrita* · _cursiva_ · ~tachado~</div>

        <div class="group-chats__body">
          <div v-if="loading && !selectedGroupId" class="group-chats__status">
            <v-progress-circular indeterminate color="primary" size="28" width="2" />
          </div>
          <div v-else-if="!selectedGroupId" class="group-chats__status text-body-2 text-medium-emphasis">
            No hay chats de grupo disponibles.
          </div>
          <CommunityGroupChatPanel
            v-else
            :key="selectedGroupId"
            :group-id="selectedGroupId"
            variant="overlay"
            hide-header
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import CommunityGroupChatPanel from '@/components/community/CommunityGroupChatPanel.vue'
import { useGroupChatsStore } from '@/stores/groupChats'

export default defineComponent({
  name: 'GroupChatsPanel',

  components: {
    CommunityGroupChatPanel,
  },

  setup() {
    const router = useRouter()
    const groupChatsStore = useGroupChatsStore()
    const {
      isOpen,
      loading,
      selectedGroupId,
      activeGroups,
      selectedGroup,
      unreadByGroupId,
    } = storeToRefs(groupChatsStore)

    const selectedGroupName = computed(() => selectedGroup.value?.name || 'Chats de grupo')

    const unreadCountFor = (groupId: string) => unreadByGroupId.value[groupId] || 0

    const close = () => {
      groupChatsStore.close()
    }

    const selectGroup = (groupId: string) => {
      groupChatsStore.selectGroup(groupId)
    }

    const goToGroup = async () => {
      const groupId = selectedGroupId.value
      if (!groupId) return
      groupChatsStore.close()
      await router.push({ name: 'community-group', params: { groupId } })
    }

    return {
      isOpen,
      loading,
      selectedGroupId,
      activeGroups,
      selectedGroupName,
      unreadCountFor,
      close,
      selectGroup,
      goToGroup,
    }
  },
})
</script>

<style scoped>
.group-chats {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 2200;
  width: min(440px, calc(100vw - 32px));
  pointer-events: none;
}

.group-chats::before {
  content: '';
  position: absolute;
  inset: -48px -36px -32px;
  z-index: 0;
  border-radius: 48px;
  background: radial-gradient(
    ellipse 90% 80% at 50% 55%,
    rgba(var(--v-theme-primary), 0.4) 0%,
    rgba(var(--v-theme-primary), 0.18) 45%,
    transparent 74%
  );
  filter: blur(26px);
  opacity: 0.95;
  pointer-events: none;
}

.group-chats__panel {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  height: min(640px, calc(100vh - 96px));
  background: rgb(var(--v-theme-surface));
  border-radius: 16px;
  box-shadow:
    0 0 0 1px rgba(var(--v-theme-primary), 0.08),
    0 0 32px rgba(var(--v-theme-primary), 0.28),
    0 0 64px rgba(var(--v-theme-primary), 0.14),
    0 2px 8px rgba(0, 0, 0, 0.06),
    0 16px 40px rgba(0, 0, 0, 0.14),
    0 32px 64px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.group-chats__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 56px;
  padding: 8px 8px 8px 12px;
  background: rgb(var(--v-theme-primary));
  color: white;
}

.group-chats__title,
.group-chats__switch-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.group-chats__title {
  flex: 1;
  min-width: 0;
  padding-left: 4px;
}

.group-chats__switch {
  flex: 1;
  min-width: 0;
  padding-inline: 8px;
}

.group-chats__go {
  text-transform: none;
  letter-spacing: normal;
}

.group-chats__header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.group-chats__switch-label {
  max-width: 240px;
}

.group-chats__menu {
  max-height: 280px;
  overflow-y: auto;
}

.group-chats__hint {
  flex-shrink: 0;
  padding: 6px 16px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.group-chats__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.group-chats__status {
  margin: auto;
  padding: 24px 16px;
  text-align: center;
}

.group-chats-slide-enter-active,
.group-chats-slide-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
}

.group-chats-slide-enter-from,
.group-chats-slide-leave-to {
  opacity: 0;
  transform: translateY(28px) scale(0.96);
}

.group-chats-slide-enter-to,
.group-chats-slide-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@media (max-width: 600px) {
  .group-chats {
    right: 12px;
    bottom: 12px;
    width: calc(100vw - 24px);
  }

  .group-chats__panel {
    height: min(72vh, calc(100vh - 80px));
  }

  .group-chats__switch-label {
    max-width: 180px;
  }
}
</style>
